import React, { useMemo, useState } from "react";
import { Box } from "@mui/material";
import SearchBar from "../../../components/common/SearchBar/SearchBar";
import FilterBar from "../../../components/common/FilterBar/FilterBar";
import PaginantionComponent from "../../../components/common/Pagination/PaginationComponent";
import { nextPageClick, previouPageClick } from "../../../utils/pagination";
import DataTable from "../../../components/common/DataTable/DataTableSorting";
import classes from "./VendorVerificationTable.module.css";
import { formatLocalDateTime } from "../../../utils/dateTime";

const BulkVerificationTable = ({
  data,
  isLoading,
  errorMessage,
  searchQuery,
  onSearchChange,
  filterValue,
  filterOptions,
  onFilterChange,
  pageNumber,
  paginationData,
  setPageNumber,
}) => {
  const [sortingColumnName, setSortingColumnName] = useState(null);
  const [sortingOrder, setSortingOrder] = useState(null);

  const headings = useMemo(
    () => [
      { label: "Supplier ID", field: "vendor_code", sortable: false },
      { label: "Email", field: "vendor_mail", sortable: false },
      {
        label: "Doc Type",
        field: "document_type",
        sortable: false,
        renderCell: (row) =>
          row?.document_type ? String(row.document_type).toUpperCase() : "-",
      },
      { label: "Status", field: "overall_status", sortable: false },
      {
        label: "Verified Name",
        field: "pan_verified_name",
        sortable: false,
        renderCell: (row) =>
          row?.pan_verified_name ||
          row?.gst_trade_name ||
          row?.msme_enterprise_name ||
          row?.bank_holder_name ||
          row?.cin_company_name ||
          "-",
      },
      {
        label: "Created Date",
        field: "uploaded_on",
        sortable: true,
        renderCell: (row) => {
          const raw =
            row?.uploaded_on ||
            row?.created_on ||
            row?.created_at ||
            row?.uploadedAt ||
            row?.uploaded_date ||
            "";

          if (!raw) return "-";
          return formatLocalDateTime(raw);
        },
      },
    ],
    []
  );

  const sortedData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    if (!sortingColumnName) return data;

    const order = sortingOrder === "desc" ? "desc" : "asc";
    const field = sortingColumnName;

    const safeLower = (v) => String(v ?? "").toLowerCase();

    const sorted = [...data].sort((a, b) => {
      const getValue = (row) => {
        if (field === "uploaded_on") {
          return (
            row?.uploaded_on ||
            row?.created_on ||
            row?.created_at ||
            row?.uploadedAt ||
            row?.uploaded_date ||
            ""
          );
        }
        return row?.[field];
      };

      const av = getValue(a);
      const bv = getValue(b);

      // Date sorting (backend sends timestamps/ISO for uploaded_on etc.)
      if (field === "uploaded_on" || field === "created_on" || field === "created_at") {
        const at = Date.parse(av);
        const bt = Date.parse(bv);
        if (!Number.isNaN(at) && !Number.isNaN(bt)) return at - bt;
      }

      const an = typeof av === "number" ? av : Number(av);
      const bn = typeof bv === "number" ? bv : Number(bv);

      const isNumberSort = !Number.isNaN(an) && !Number.isNaN(bn);
      if (isNumberSort) return an - bn;

      return safeLower(av).localeCompare(safeLower(bv));
    });

    return order === "desc" ? sorted.reverse() : sorted;
  }, [data, sortingColumnName, sortingOrder]);

  const itemsPerPage = paginationData?.current_page_count ?? sortedData.length;
  const totalPages = paginationData?.page_count ?? 1;
  const currentPage = 1;

  return (
    <div className={classes.wrapper}>
      <div className={classes.toolbar}>
        <div className={classes.toolbarLeft}>
          <SearchBar
            value={searchQuery}
            placeholder="Search Supplier code and email"
            onChange={onSearchChange}
            customClass={classes.searchResponsive}
          />
        </div>
        <div className={classes.toolbarRight}>
          <FilterBar
            filterValue={filterValue}
            onFilterChange={onFilterChange}
            options={filterOptions}
            customClass={classes.filterFix}
          />
        </div>
      </div>

      <div className={classes.tableWrap}>
        <DataTable
          headings={headings}
          tableData={sortedData}
          tableSize={"100%"}
          actions={false}
          sorting={true}
          errorMessage={errorMessage}
          isLoading={isLoading}
          setSortingColumnName={setSortingColumnName}
          sortingColumnName={sortingColumnName}
          setSortingOrder={setSortingOrder}
          sortingOrder={sortingOrder}
        />

        {!isLoading && totalPages > 0 && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              mt: 2,
              pr: { xs: 0, md: 8 }, // avoid floating action buttons overlapping pagination
            }}
          >
            <PaginantionComponent
              pageNumber={pageNumber}
              currentPage={currentPage || 0}
              itemsPerPage={itemsPerPage || 0}
              totalItems={totalPages || 0}
              previouPageClick={() => previouPageClick(pageNumber, setPageNumber)}
              localStorageKey={"bulk_vendor_verification_page_number"}
              nextPageClick={() => nextPageClick(pageNumber, setPageNumber, paginationData)}
              setPageNumber={setPageNumber}
              disablePrevious={parseInt(pageNumber) === 1 || pageNumber === ""}
              disableNext={parseInt(pageNumber) === totalPages || !paginationData || pageNumber === ""}
            />
          </Box>
        )}
      </div>
    </div>
  );
};

export default BulkVerificationTable;
